<template>
    <div class="threshold-settings">
        <div class="info-block">
            Fleet Thresholds Settings
        </div>

        <v-container fluid>
            <v-layout row>
                <v-flex md6 class="section">
                    <div class="section-title">Speeding Thresholds</div>

                    <v-form
                    ref="form"
                    >
                    <v-layout column v-for="(speeding, index) in settings.thresholds.speedingThresholds" :key="`speeding_${speeding.key}`">
                        <v-flex>
                            <div class="label">
                                {{ mapVehicleType(speeding.key) }} Speeding Thresholds
                            </div>
                        </v-flex>
                        <v-layout row>
                                <v-flex md4>
                            <div class="settings__green">
                                <v-text-field
                                    v-model.number="settings.thresholds.speedingThresholds[index].value.green"
                                    required
                                    mask="###"
                                    suffix="%"
                                    :min="0"
                                    :rules="lowestSpeed(index)"
                                    outline
                                    single-line
                                ></v-text-field>
                            </div>
                                </v-flex>
                                <v-flex md4>
                            <div class="settings__amber">
                                <v-text-field
                                    v-model.number="settings.thresholds.speedingThresholds[index].value.amber"
                                    required
                                    mask="###"
                                    suffix="%"
                                    :rules="mediumSpeed(index)"
                                    outline
                                    single-line
                                ></v-text-field>
                            </div>
                                </v-flex>
                                <v-flex md4>
                            <div class="settings__red">
                                <v-text-field
                                    v-model.number="settings.thresholds.speedingThresholds[index].value.red"
                                    required
                                    mask="###"
                                    suffix="%"
                                    :rules="highestSpeed(index)"
                                    outline
                                    single-line
                                ></v-text-field>
                            </div>
                            </v-flex>
                        </v-layout>
                    </v-layout>
                </v-form>
                </v-flex>
                <v-flex md6 class="section">
                    <div class="section-title">Driving Thresholds</div>
                    <v-layout column v-for="(driving, index) in settings.thresholds.drivingThresholdsLevels" :key="`driving_${driving.key}`">
                        <v-flex>
                            <div class="label">
                                {{ mapVehicleType(driving.key) }} Driving Thresholds
                            </div>
                        </v-flex>
                        <v-flex>
                            <option-buttons :type-index="index" :config="getThresholdLevels" :value="driving.value.thresholdLevel" :handle-change="handleDrivingThresholdsChange"></option-buttons>
                        </v-flex>
                    </v-layout>
                    <v-layout row>
                        <v-flex md6 text-center>
                            <div class="label label--checkbox">
                                Classify Stationary Events
                            </div>
                        </v-flex>
                        <v-flex md6>
                            <v-checkbox v-model="settings.classifyStationaryEvents"></v-checkbox>
                        </v-flex>
                    </v-layout>
                    <v-layout row>
                        <v-flex md6 text-center>
                            <div class="label label--checkbox">
                                Snapped Journeys
                            </div>
                        </v-flex>
                        <v-flex md6>
                            <v-checkbox v-model="settings.snappedJourneys"></v-checkbox>
                        </v-flex>
                    </v-layout>
                    <v-layout justify-end row>
                        <sp-button @click.native="updateFleetSettings" text="Save" icon="download"></sp-button>
                    </v-layout>

                </v-flex>
            </v-layout>
        </v-container>
        <sp-loader v-if="isLoading"></sp-loader>
    </div>
</template>

<script>
import SpButton from '@/components/forms/Button'
import FleetSettingsApi from '@/api/FleetSettingsApi'
import { searchEnumById, calculateSpeedOrDistanceUnit, JSONClone } from '@/helpers/Utils'
import { mapGetters } from 'vuex'
import OptionButtons from '@/components/atoms/OptionButtons'
import toast from '@/plugins/izitoast'
import SpLoader from '@/components/atoms/Loader'

const fleetSettingsApi = new FleetSettingsApi()

export default {
    name: 'ThresholdSettings',
    mounted () {
        this.id = this.$route.params.fleetUuid
        this.getFleetSettings()
    },
    components: {
        OptionButtons,
        SpButton,
        SpLoader
    },
    data () {
        return {
            valid: false,
            id: null,
            isLoading: false,
            settings: {
                snappedJourneys: false,
                classifyStationaryEvents: false,
                thresholds: {
                    speedingThresholds: [],
                    drivingThresholdsLevels: []
                }
            }
        }
    },
    computed: {
        lowestSpeed (index) {
            return (index) => {
                const rules = []
                rules.push(v => (/^\d+$/.test(v)) || 'Threshold cannot be empty')
                if (this.settings.thresholds.speedingThresholds[index].value.green >= this.settings.thresholds.speedingThresholds[index].value.amber || this.settings.thresholds.speedingThresholds[index].value.green >= this.settings.thresholds.speedingThresholds[index].value.red) {
                    rules.push(v => 'This value must be the smallest one')
                }
                return rules
            }
        },
        mediumSpeed (index) {
            return (index) => {
                const rules = []
                rules.push(v => (/^\d+$/.test(v)) || 'Threshold cannot be empty')
                if (this.settings.thresholds.speedingThresholds[index].value.amber <= this.settings.thresholds.speedingThresholds[index].value.green || this.settings.thresholds.speedingThresholds[index].value.amber >= this.settings.thresholds.speedingThresholds[index].value.red) {
                    rules.push(v => 'This value must be the middle one')
                }
                return rules
            }
        },
        highestSpeed (index) {
            return (index) => {
                const rules = []
                rules.push(v => (/^\d+$/.test(v)) || 'Threshold cannot be empty')
                if (this.settings.thresholds.speedingThresholds[index].value.red <= this.settings.thresholds.speedingThresholds[index].value.amber || this.settings.thresholds.speedingThresholds[index].value.red <= this.settings.thresholds.speedingThresholds[index].value.green) {
                    rules.push(v => 'This value must be the highest one')
                }
                return rules
            }
        },
        ...mapGetters('dictionaryModule', [
            'getVehicleTypes',
            'getThresholdLevels'
        ])
    },
    methods: {
        mapVehicleType (vehicleType) {
            let vehicle = searchEnumById(this.getVehicleTypes, vehicleType)

            return vehicle || '-'
        },
        async getFleetSettings () {
            this.isLoading = true

            const response = await fleetSettingsApi.getFleetSettings(this.id)

            if (!response.isSuccess) {
                this.isLoading = false
                return
            }

            this.settings = response.payload
            this.isLoading = false
        },
        async updateFleetSettings () {
            if (!this.$refs.form.validate()) {
                toast.error('Form is not valid')
                return
            }

            let settings = JSONClone(this.settings)

            const response = await fleetSettingsApi.updateFleetSettings(this.id, settings)

            if (!response.isSuccess || !response.payload) {
                return
            }

            await this.getFleetSettings()
            toast.success('Thresholds updated successfully')
        },
        mapSettings (setting, form) {
            for (let name in setting) {
                if (setting.hasOwnProperty(name)) {
                    let value = setting[name].value

                    if (2 === setting[name].type) {
                        value = ('true' === value)
                    }

                    form[name] = value
                }
            }
        },
        handleDrivingThresholdsChange: function (index, value) {
            this.settings.thresholds.drivingThresholdsLevels[index].value.thresholdLevel = value
        }
    }
}
</script>

<style scoped lang="scss">
.threshold-settings {
    .section-title {
        font-weight: bold;
        font-size: 20px;
        margin-bottom: 10px;
    }
    .info-block {
        margin-bottom: 20px;
    }

    .label {
        position: relative;
        margin-bottom: 5px;

        &--checkbox {
            top: 12px;
            margin-bottom: 0;
        }

    }

    .section {
        //border-right: 2px solid $primary;
        padding: 0 10px;

        &:last-child {
            border-right-color: transparent;
        }
    }
}

.settings {
    &__green {
        margin: 0 5px 0 0;
        ::v-deep .v-input .v-input__slot {
            border-color: $success;
        }
    }

    &__amber {
        margin: 0 5px 0 0;
        ::v-deep .v-input .v-input__slot {
            border-color: $warning;
        }
    }

    &__red {
        ::v-deep .v-input .v-input__slot {
            border-color: $danger;
        }
    }
}
</style>
